






































import { mdiEye, mdiEyeOff } from '@mdi/js';

import { computed, defineComponent, onBeforeUnmount, PropType, ref, unref, watch } from '@vue/composition-api';
import { useContext } from '@berlin-front/common/composables/context';
import { ThemeContext } from '@berlin-front/common/services/ThemeContext';
import { bemx } from '@berlin-front/common/utils';
import PatchedVTextField from '@berlin-front/common/components/elements/PatchedVTextField';

/**
 * ## パスワード入力フィールド
 */
export default defineComponent({
  name: 'AppPassword',
  components: {
    PatchedVTextField,
  },

  props: {
    /**
     * 無効化。
     */
    disabled: {
      type: Boolean,
      default: false,
    },

    /**
     * ディテール表示を消す。
     */
    hideDetails: {
      type: Boolean,
      default: false,
    },

    /**
     * input要素に指定される一意のid
     */
    id: {
      type: String,
      required: false,
      default: () => undefined,
    },

    /**
     * エラーメッセージ。
     *
     * バリデーションエラー時に表示するメッセージ。
     * バリデーションオブジェクトの状態によって変化する。
     * */
    errorMessages: {
      type: Array as PropType<string[] | unknown[]>,
      default: null,
    },

    /**
     * ヒント。
     *
     * フォーカスで補足説明が表示される。
     */
    hint: {
      type: String,
      default: '',
    },

    /**
     * ラベル。
     *
     * テキストフィールドの項目ラベルとして使用される。
     * 正式なフィールド名でなく、簡易的な名称をラベルに設定しても問題なし。
     * また、テキストフィールドによってはラベル名の記載がないものもある。
     */
    label: {
      type: String,
      default: '',
    },

    /**
     * メッセージ非表示。
     *
     * 指定するとバリデーションメッセージを表示領域ごと消す。
     */
    noMessage: {
      type: Boolean,
      default: false,
    },

    /**
     * プレースホルダー
     *
     * 必要に応じて入力例を設定する。
     */
    placeholder: {
      type: String,
      default: '',
    },

    /**
     * 読取専用。
     */
    readonly: {
      type: Boolean,
      default: false,
    },

    /**
     * 最大長
     */
    maxLength: {
      type: Number,
      required: false,
      default: () => undefined,
    },

    /**
     * 入力値。
     *
     * v-modelで指定した値が連動される。
     */
    value: {
      type: String,
      default: '',
    },

    /**
     * クリアボタンを使用するか。
     */
    clearable: {
      type: Boolean,
      default: false,
    },

    /**
     * パスワード入力を補完するかどうか。
     *
     * 通常、
     * - ログインの場合には補完する。
     * - 登録の場合は新規パスワードになるため補完しない。
     */
    newPassword: {
      type: Boolean,
      required: false,
      default: false,
    },

    /**
     * 非表示モードのマスク文字。
     */
    mask: {
      type: String,
      required: false,
      default: '•',
    },
  },

  emits: [
    /**
     * inputイベント
     */
    'input',
    /**
     * blurイベント
     * @type {Event}
     */
    'blur',
  ],

  setup(props, { listeners }) {
    /**
     * パスワード表示領域
     */
    const passwordDisplayRef = ref<HTMLDivElement>();
    /**
     * マスク状態。
     */
    const passwordMask = ref(true);
    /**
     * マスク状態のトグルアクション
     */
    const togglePasswordMask = () => {
      passwordMask.value = !passwordMask.value;
    };
    /**
     * 切り替えアイコンボタンのアイコン。
     *
     * マスク状態によって見た目が変わる。
     */
    const icon = computed(() => (unref(passwordMask) ? mdiEyeOff : mdiEye));
    /**
     * vuetifyの入力要素のref
     */
    const componentRef = ref<{ $refs?: { input?: HTMLInputElement } }>();
    /**
     * パスワード入力要素。
     */
    const inputRef = computed(() => componentRef.value?.$refs?.input || undefined);

    let blurTimerHandle = NaN;
    /**
     * フォーカス状態。
     */
    const focused = ref(false);
    const onFocus = () => {
      focused.value = true;
      window.clearTimeout(blurTimerHandle);
    };
    const onBlur = () => {
      // blur状態が0.2秒つづいたらfocused stateがfalseになる。
      blurTimerHandle = window.setTimeout(() => {
        focused.value = false;
        if (passwordDisplayRef.value) {
          passwordDisplayRef.value.scrollLeft = 0;
        }
      }, 200);
    };
    /**
     * オートコンプリート
     */
    const passwordAutoComplete = computed(() => (props.newPassword ? 'new-password' : 'current-password'));

    /**
     * 選択実行中
     */
    const selecting = ref(false);

    /**
     * パスワードフィールド上のテキスト選択開始位置
     */
    const selectionStart = ref(0);
    /**
     * テキスト選択終了位置
     */
    const selectionEnd = ref(0);
    /**
     * キャレット位置
     */
    const caretPosition = ref(0);
    /**
     * 選択状態
     */
    const hasSelection = computed(() => selectionStart.value !== selectionEnd.value);
    /**
     * エラー表示中かどうか
     */
    const hasErrors = computed(() => (props.errorMessages || []).length > 0);
    /**
     * 表示用データ
     */
    const displayChars = computed(() =>
      [...(props.value || '').split('').map((char) => (passwordMask.value ? props.mask : char)), ''].map(
        (char, index) => ({
          key: `${index}-${char}`,
          char,
          class: bemx('AppPassword__DisplayChar', {
            selected:
              (selecting.value || focused.value) &&
              hasSelection.value &&
              selectionStart.value <= index &&
              index < selectionEnd.value,
            activeCaret: !hasSelection.value && focused.value && !hasErrors.value && index === caretPosition.value,
            errorCaret: !hasSelection.value && focused.value && hasErrors.value && index === caretPosition.value,
          }),
        }),
      ),
    );
    const isEmpty = computed(() => (props.value ?? '').length === 0);

    /**
     * 不可視password入力の選択状態を表示に同期する。
     */
    const applyInputSelectionToDisplaySelection = () => {
      if (!inputRef.value) return;

      selectionStart.value = inputRef.value.selectionStart || 0;
      selectionEnd.value = inputRef.value.selectionEnd || 0;
      caretPosition.value =
        (inputRef.value.selectionDirection === 'backward'
          ? inputRef.value.selectionStart
          : inputRef.value.selectionEnd) || 0;
    };

    /**
     * 選択範囲の変更理由を知るために直前の入力内容を保持します。
     */
    let prevValue = props.value || '';
    /**
     * 不可視password入力の選択状態の変化をハンドルする。
     *
     * - 不可視password入力の選択状態を表示に同期する。
     * - キャレットが入力領域からオーバーフローした際にスクロールさせる。
     */
    const onInputSelectionChange = () => {
      // documentのselectionchangeイベントを使用するため、フォーカスしてる場合のみに限定する。
      if (!focused.value) return;

      // 不可視password入力の選択状態を表示に同期する。
      applyInputSelectionToDisplaySelection();

      if (!passwordDisplayRef.value) return;
      /** キャレットを表示してる要素 */
      const caretPositionElement = passwordDisplayRef.value.children.item(caretPosition.value);
      /** 際末尾の要素（文字を格納しない末尾のキャレット表示要素） */
      const lastElement = passwordDisplayRef.value.children.item(passwordDisplayRef.value.children.length - 1);
      if (!(caretPositionElement instanceof HTMLElement) || !(lastElement instanceof HTMLElement)) return;

      /** 現在のスクロール表示範囲の左端。 */
      const displayLeft = passwordDisplayRef.value.scrollLeft;
      /** 表示領域の幅。 */
      const displayWidth = passwordDisplayRef.value.offsetWidth;
      /** 現在のスクロール表示範囲の右端。 */
      const displayRight = displayLeft + displayWidth;
      /** キャレットが表示されている座標 */
      const left = caretPositionElement.offsetLeft;
      /** 最大スクロール位置 */
      const maxScrollLeft = Math.max(lastElement.offsetLeft - displayWidth + 1, 0);
      /** 値が更新されてるかどうか */
      const valueUpdated = prevValue !== inputRef.value?.value ?? '';
      prevValue = inputRef.value?.value ?? '';

      if (left < displayLeft) {
        // 左に溢れてる場合
        if (valueUpdated) {
          // 入力、削除による更新の場合キャレットの移動は最低限にする。
          passwordDisplayRef.value.scrollLeft = Math.max(0, left);
        } else {
          // カーソルによる移動の場合、画面の半分スクロールさせる。
          passwordDisplayRef.value.scrollLeft = Math.max(0, left - displayWidth / 2);
        }
      } else if (displayRight < left) {
        // 右に溢れてる。
        if (valueUpdated) {
          passwordDisplayRef.value.scrollLeft = left - displayWidth + 1;
        } else {
          passwordDisplayRef.value.scrollLeft = Math.min(Math.max(0, left - displayWidth / 2), maxScrollLeft);
        }
      }
    };

    // 隠されてるパスワード入力要素に対するイベントハンドラ
    const setInputEventHandler = (inputElem: HTMLInputElement) => {
      applyInputSelectionToDisplaySelection();
      // フォーカス状態の記録。
      inputElem.addEventListener('focus', onFocus);
      inputElem.addEventListener('blur', onBlur);
      // パスワードフォームの選択状態およびキャレット移動のハンドリング。
      // InputElementのselectイベントはキャレトの移動を検出できないためselectionchangeを使用。
      // ハンドラ内部でフォーカス状態を見ることで、入力要素限定で動作するようにしている。
      document.addEventListener('selectionchange', onInputSelectionChange);
      // 削除の際はselectionchangeが発生しないようなのでinputイベントでも仕掛ける。
      inputElem.addEventListener('input', onInputSelectionChange);
    };
    const removeInputElementEventHandler = (inputElement: HTMLInputElement) => {
      inputElement.removeEventListener('input', onInputSelectionChange);
      document.removeEventListener('selectionchange', onInputSelectionChange);
      inputElement.removeEventListener('blur', onBlur);
      inputElement.removeEventListener('focus', onFocus);
    };
    watch(
      [inputRef] as const,
      ([input], [oldInput]) => {
        if (input) {
          setInputEventHandler(input);
        }
        if (oldInput) {
          removeInputElementEventHandler(oldInput);
        }
      },
      { immediate: true },
    );
    onBeforeUnmount(() => {
      if (inputRef.value) {
        removeInputElementEventHandler(inputRef.value);
      }
    });

    let lastValidRange: Range | undefined;
    const onSelectStart = (_ev: Event) => {
      selecting.value = true;
    };
    const onSelectEnd = (_ev: MouseEvent) => {
      if (!selecting.value) return;
      selecting.value = false;

      const selection = document.getSelection();
      if (!selection || !(selection.rangeCount > 0) || passwordDisplayRef.value === undefined || !inputRef.value)
        return;

      selecting.value = false;
      const range = selection.getRangeAt(0);
      const direction = selection.anchorNode === range.startContainer ? 'forward' : 'backward';
      const startIndex =
        Number(
          (range.startContainer instanceof HTMLElement
            ? range.startContainer.dataset?.index
            : range.startContainer.parentElement?.dataset?.index) ?? '0',
        ) + range.startOffset;
      const endIndex =
        Number(
          (range.endContainer instanceof HTMLElement
            ? range.endContainer.dataset?.index
            : range.endContainer.parentElement?.dataset?.index) ?? '0',
        ) + range.endOffset;

      selection.removeAllRanges();
      inputRef.value.focus();
      selectionStart.value = startIndex;
      selectionEnd.value = endIndex;
      caretPosition.value = direction === 'forward' ? startIndex : endIndex;
      inputRef.value.selectionStart = startIndex;
      inputRef.value.selectionEnd = endIndex;
      inputRef.value.selectionDirection = direction;
      lastValidRange = undefined;
    };
    /**
     * ディスプレイ部の選択状態の変化をトリガーして、 表示上の選択範囲を調整します。
     */
    const onSelectionChange = () => {
      if (focused.value) return;

      const selection = document.getSelection();
      if (!selection || !(selection.rangeCount > 0) || !selecting.value) return;

      // 選択実行中、入力欄から選択がはみ出ないようにする。これは通常の入力フォームの仕様に準ずる。
      const range = selection.getRangeAt(0);
      if (range.commonAncestorContainer === passwordDisplayRef.value) {
        lastValidRange = range;
      } else if (range.commonAncestorContainer instanceof Text) {
        lastValidRange = undefined;
      } else {
        // はみ出てる場合、選択をクリアし最後の正しいrangeをセットする。
        selection.removeAllRanges();
        if (lastValidRange !== undefined) {
          selection.addRange(lastValidRange);
        } else {
          lastValidRange = undefined;
        }
      }
      const startIndex =
        Number(
          (range.startContainer instanceof HTMLElement ? range.startContainer : range.startContainer.parentElement!)
            .dataset.index,
        ) + range.startOffset;
      const endIndex =
        Number(
          (range.endContainer instanceof HTMLElement ? range.endContainer : range.endContainer.parentElement!).dataset
            .index,
        ) + range.endOffset;

      selectionStart.value = startIndex;
      selectionEnd.value = endIndex;
      caretPosition.value = NaN;
    };
    /**
     * 要素クリック時のハンドラ。
     *
     * 動作した時にキャレットが設定されてない場合末尾に末尾にキャレットが配置される。
     */
    const onClickPasswordDisplayRef = (ev: MouseEvent) => {
      const selected =
        inputRef.value === document.activeElement &&
        typeof inputRef.value?.selectionStart === 'number' &&
        typeof inputRef.value?.selectionEnd === 'number' &&
        inputRef.value?.selectionStart !== inputRef.value?.selectionEnd;
      if (!focused.value && ev.target === passwordDisplayRef.value && inputRef.value && !selected) {
        inputRef.value.selectionStart = inputRef.value.value.length || 0;
        inputRef.value.selectionEnd = inputRef.value.selectionStart;
        inputRef.value.selectionDirection = 'none';
        inputRef.value.focus();
      }
    };
    /**
     * ダブルクリック時のハンドラ。全選択。
     */
    const onDoubleClickPasswordDisplayRef = () => {
      if (inputRef.value) {
        inputRef.value.focus();
        inputRef.value.selectionStart = 0;
        inputRef.value.selectionEnd = inputRef.value.value.length || 0;
        inputRef.value.selectionDirection = 'none';
      }
    };
    const setPasswordDisplayHandler = (display: HTMLDivElement) => {
      // ディスプレイの選択状態 -> 見えないpassword入力フォームの選択除隊 への同期。
      display.addEventListener('selectstart', onSelectStart);
      document.addEventListener('mouseup', onSelectEnd);
      document.addEventListener('selectionchange', onSelectionChange);
      // クリック、ダブルクリック時に見えないpassword入力フォームにフォーカスさせる。
      // クリックに関してはselectionchangeと排他で動作するようハンドラ内部でフィルタしてる。
      display.addEventListener('click', onClickPasswordDisplayRef);
      display.addEventListener('dblclick', onDoubleClickPasswordDisplayRef);
    };
    const removePasswordDisplayHandler = (display: HTMLDivElement) => {
      display.removeEventListener('dblclick', onDoubleClickPasswordDisplayRef);
      display.removeEventListener('click', onClickPasswordDisplayRef);
      document.removeEventListener('selectionchange', onSelectionChange);
      document.removeEventListener('mouseup', onSelectEnd);
      display.removeEventListener('selectstart', onSelectStart);
    };
    watch(
      [passwordDisplayRef],
      ([display], [oldDisplay]) => {
        if (display) {
          setPasswordDisplayHandler(display);
        }
        if (oldDisplay) {
          removePasswordDisplayHandler(oldDisplay);
        }
      },
      { immediate: true },
    );
    onBeforeUnmount(() => {
      if (passwordDisplayRef.value) {
        removePasswordDisplayHandler(passwordDisplayRef.value);
      }
    });

    const { themeParameter } = useContext(ThemeContext);
    const primaryColor = computed(() => themeParameter.value.primaryColor);

    const vuetifyComponentClassName = computed(() => bemx('AppPassword', { noMessage: props.noMessage }));
    const displayAreaClassName = computed(() =>
      bemx('AppPassword__Display', { buttons: props.clearable && (props.value?.length ?? 0) > 1 ? 2 : 1 }),
    );
    const displayContentClassName = computed(() => bemx('AppPassword__DisplayContent', { isEmpty: isEmpty.value }));

    return {
      passwordAutoComplete,
      icon,
      passwordDisplayRef,
      vuetifyComponentClassName,
      displayAreaClassName,
      primaryColor,
      togglePasswordMask,
      passwordMask,
      listeners,
      selectionStart,
      selectionEnd,
      componentRef,
      displayChars,
      displayContentClassName,
    };
  },
});
